import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "https://ims.holdthechild.net";

const EditInvoiceModal = ({ invoice, closeModal }) => {
  const [formData, setFormData] = useState({
    invoiceNumber: invoice.invoiceNumber || "",
    invoiceDate: invoice.invoiceDate
      ? new Date(invoice.invoiceDate).toISOString().split("T")[0]
      : "",
    dueDate: invoice.dueDate
      ? new Date(invoice.dueDate).toISOString().split("T")[0]
      : "",
    amount: invoice.amount || "",
    description: invoice.description || "",
  });
  const [status, setStatus] = useState(invoice.status || "Pending Review");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // ✅ Update invoice details
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) =>
        data.append(key, value)
      );
      if (file) data.append("invoiceFile", file);

      await axios.put(`${API_URL}/api/vendor-invoices/${invoice._id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // ✅ Update status separately
      await axios.patch(
        `${API_URL}/api/vendor-invoices/${invoice._id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Invoice updated ✅");
      closeModal();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update invoice ❌");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Render preview of existing file
  const renderFilePreview = () => {
    if (!invoice.invoiceFile) return null;

    const fileUrl = `${API_URL}/${invoice.invoiceFile}`;
    const lowerName = invoice.invoiceFile.toLowerCase();

    if (lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg") || lowerName.endsWith(".png") || lowerName.endsWith(".tiff")) {
      return (
        <div className="mb-2">
          <p className="text-sm text-gray-600">Current File:</p>
          <img
            src={fileUrl}
            alt="Invoice Preview"
            className="max-h-40 mt-2 border rounded"
          />
        </div>
      );
    }

    if (lowerName.endsWith(".pdf")) {
      return (
        <div className="mb-2">
          <p className="text-sm text-gray-600">Current File:</p>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            View PDF
          </a>
        </div>
      );
    }

    return (
      <div className="mb-2">
        <p className="text-sm text-gray-600">Current File:</p>
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          Download File
        </a>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Edit Invoice</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            name="invoiceNumber"
            placeholder="Invoice Number"
            value={formData.invoiceNumber}
            onChange={handleChange}
            required
            className="w-full border p-2 rounded"
          />
          <input
            type="date"
            name="invoiceDate"
            value={formData.invoiceDate}
            onChange={handleChange}
            required
            className="w-full border p-2 rounded"
          />
          <input
            type="date"
            name="dueDate"
            value={formData.dueDate}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          />
          <input
            type="number"
            name="amount"
            placeholder="Amount"
            value={formData.amount}
            onChange={handleChange}
            required
            className="w-full border p-2 rounded"
          />
          <textarea
            name="description"
            placeholder="Description"
            value={formData.description}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          />

          {/* ✅ Status Dropdown */}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full border p-2 rounded"
          >
            <option value="Pending Review">Pending Review</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Paid">Paid</option>
          </select>

          {/* ✅ Show existing file preview */}
          {renderFilePreview()}

          {/* ✅ Upload new file (replace option) */}
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.tiff"
            onChange={handleFileChange}
            className="w-full"
          />

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 rounded text-white ${
                loading
                  ? "bg-orange-400 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700"
              }`}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInvoiceModal;
